import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { waLink, WA_MESSAGES, trackWhatsApp } from "@/lib/whatsapp";
import WhatsAppIcon from "./WhatsAppIcon";

const LINKS = [
  { label: "Experiências", href: "#passeios", testid: "nav-link-tours" },
  { label: "Sobre Mim", href: "#sobre", testid: "nav-link-about" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
];

// Na home, âncoras nativas (Lenis cuida do scroll). Fora dela, Link do router para "/#secao".
const SectionLink = ({ href, isHome, children, ...props }) =>
  isHome ? (
    <a href={href} {...props}>{children}</a>
  ) : (
    <Link to={`/${href}`} {...props}>{children}</Link>
  );

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = useLocation().pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-[#0D1117]/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <SectionLink href="#top" isHome={isHome} data-testid="nav-brand-logo" className="group flex items-baseline gap-2">
          <span className="font-display text-xl font-extrabold tracking-tight text-stone-50">
            DARLEI
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">
            Guia RJ
          </span>
        </SectionLink>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((link) => (
            <SectionLink
              key={link.href}
              href={link.href}
              isHome={isHome}
              data-testid={link.testid}
              className="text-sm font-medium text-stone-300 transition-colors duration-300 hover:text-amber-400"
            >
              {link.label}
            </SectionLink>
          ))}
          <a
            href={waLink(WA_MESSAGES.default)}
            onClick={() => trackWhatsApp("menu_topo")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-[#20BD5A]"
          >
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </a>
        </div>

        <button
          className="text-stone-100 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Abrir menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0D1117]/95 px-5 pb-6 pt-4 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((link) => (
              <SectionLink
                key={link.href}
                href={link.href}
                isHome={isHome}
                data-testid={`${link.testid}-mobile`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-stone-200 transition-colors hover:text-amber-400"
              >
                {link.label}
              </SectionLink>
            ))}
            <a
              href={waLink(WA_MESSAGES.default)}
              onClick={() => trackWhatsApp("menu_mobile")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-whatsapp-mobile"
              className="mt-2 flex w-fit items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Falar no WhatsApp
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
